import {html, HTMLTemplateResult} from "lit";
import {customElement} from "lit/decorators/custom-element.js";

import {AbstractO2dElement} from "@o2d/shared";
import type {Subscription} from "rxjs";
import {container} from "tsyringe";
import {GIMMICKS_BOARD_UID} from "../../constants";
import {AbstractLinkviewRepository, LINKVIEW_REPOSITORY} from "../../domain/repositories/abstract-linkview.repository";
import {isGimmicksBoard} from "../../domain/gimmicks/gimmicks-board.helper";

/**
 * Mode "Gimmicks" (Anke, 2026-11-01: "ein neuer hauptmenupunkt … ganz analog zur
 * linkview-seite, wo in kacheln gimicks zur verfügung bereitgestellt werden").
 *
 * Route `/gimmicks`, hooked into the mode switch, the mode router, the start-page
 * list and `lastMode` exactly like `<o2d-linkview-view>` (feature plan 0070). No
 * sidebar list of its own (Anke, 2026-11-01).
 *
 * The page IS one Linkboard (feature plan 0071): the Gimmicks board, told apart
 * by its fixed uid. It renders `<o2d-linkview-view>` for that uid — the same view,
 * toolbar (Bearbeiten / Speichern / Abbrechen / Neue Kachel), lock and tile grid
 * as every Linkboard, no second copy. On that board the tile dialog also offers
 * the kind "Gimmick" (`dashboard-creator`). `views/index.ts` registers the
 * Linkview view already; no import of it here. Should the board be missing (the
 * migration not run on this tenant), the grid stays empty without a toolbar — so
 * the view says so in the console once the board list has arrived without it
 * (review 0071-03). An empty list stays silent: it cannot be told apart from one
 * not loaded yet.
 *
 * A new component, so it extends `AbstractO2dElement` (ADR-007; Light DOM) — moved over from
 * AbstractLitElement in 0071; styles in ralphy.css.
 */
@customElement('o2d-gimmicks-view')
export class GimmicksView extends AbstractO2dElement {

    private _boards: Subscription | null = null;

    /** Warned already — `items$` may emit its current list synchronously inside `subscribe`, before
     *  `_boards` is assigned, so the subscription cannot end itself there. */
    private _warned = false;

    public override connectedCallback(): void {
        super.connectedCallback();
        const repository = container.resolve<AbstractLinkviewRepository>(LINKVIEW_REPOSITORY);
        this._boards = repository.items$.subscribe((boards) => {
            if (!this._warned && boards.length > 0 && !boards.some((board) => isGimmicksBoard(board.uid))) {
                this._warned = true;
                console.warn(
                    `The Gimmicks board ${GIMMICKS_BOARD_UID} is missing on this tenant — `
                    + 'run migration Version20261101100000 for it (feature plan 0071).',
                );
            }
        });
    }

    public override disconnectedCallback(): void {
        this._boards?.unsubscribe();
        this._boards = null;
        super.disconnectedCallback();
    }

    public render(): HTMLTemplateResult {
        return html`<o2d-linkview-view .sid=${GIMMICKS_BOARD_UID}></o2d-linkview-view>`;
    }
}
